import React from "react";
import { DayButtonProps } from "@/lib/types";
import {
  formatMonthYear,
  getDayOfWeekShort,
  getDayOfMonth,
  isToday,
  normalizeDate,
  getDateString,
} from "@/lib/dateUtils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";

const DayButton: React.FC<DayButtonProps> = ({
  day,
  date,
  isSelected,
  isWeekend = false,
  onClick,
}) => {
  return (
    <div
      className={`flex flex-col items-center min-w-[3rem] py-2 px-1 rounded-lg cursor-pointer transition-colors
        ${isSelected ? "bg-primary text-white" : "hover:bg-gray-100"}`}
      onClick={onClick}
    >
      <span
        className={`text-xs font-medium ${
          isWeekend && !isSelected ? "text-blue-800" : ""
        }`}
      >
        {day}
      </span>
      <span className="font-mono text-lg">{date}</span>
    </div>
  );
};

interface DateNavigationProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  weekDates: Date[];
}

const DateNavigation: React.FC<DateNavigationProps> = ({
  selectedDate,
  onDateChange,
  weekDates,
}) => {
  // Create a local copy of the selected date to prevent any React batching issues
  const currentDate = new Date(selectedDate.getTime());

  // Get today's date at midnight using our utility
  const today = normalizeDate(new Date());

  // Compare dates by converting to YYYY-MM-DD format for consistent comparison
  const isSameDay = (date1: Date, date2: Date) => {
    return getDateString(date1) === getDateString(date2);
  };

  // Navigate to previous week
  const goToPreviousWeek = () => {
    // Take the first day of current week and go back 7 days
    const prevWeekDate = new Date(weekDates[0]);
    prevWeekDate.setDate(prevWeekDate.getDate() - 7);
    onDateChange(prevWeekDate);
  };

  // Navigate to next week (only if we're viewing a past week)
  const goToNextWeek = () => {
    // Take the first day of current week and go forward 7 days
    const nextWeekDate = new Date(weekDates[0]);
    nextWeekDate.setDate(nextWeekDate.getDate() + 7);

    // Only allow going up to the current week
    if (nextWeekDate <= today) {
      onDateChange(nextWeekDate);
    } else {
      // If next week would be in the future, just go to today
      onDateChange(today);
    }
  };

  // Check if we're viewing the current week
  const isCurrentWeek = () => {
    // Check if any of the days in the current week contains today's date
    return weekDates.some((date) => isSameDay(date, today));
  };

  // Check if next week navigation should be enabled
  const canGoNext = !isCurrentWeek();

  return (
    <div className="pt-4 pb-2 border-b border-gray-200">
      <div className="flex justify-between items-center mb-2 px-4">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 mr-2 text-gray-500"
            onClick={goToPreviousWeek}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <h2 className="font-heading font-semibold">
            {formatMonthYear(currentDate)}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 ml-2 text-gray-500"
            onClick={goToNextWeek}
            disabled={!canGoNext}
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
        {!isCurrentWeek() && (
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-8"
            onClick={() => onDateChange(today)}
          >
            Today
          </Button>
        )}
      </div>{" "}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {weekDates.map((date, index) => (
          <DayButton
            key={index}
            day={getDayOfWeekShort(date)}
            date={getDayOfMonth(date)}
            isSelected={isSameDay(date, currentDate)}
            isWeekend={date.getDay() === 0 || date.getDay() === 6}
            onClick={() => {
              // Normalize the date to ensure consistent date handling
              const newDate = normalizeDate(date);
              // Date click handled cleanly now
              onDateChange(newDate);
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default DateNavigation;
